/**
 * ArchivedDocumentsSection — Section dépliable « Archivés ».
 *
 * Affiche les contrats signés dont la date d'échéance + 1 jour est dépassée.
 * Réutilise le composant GenericDocSection pour chaque sous-catégorie.
 *
 * V35.9 — Tableau des membres / Voir documents.
 */
import React, { useState } from 'react';
import { Archive, ChevronDown, ChevronUp, Shield, Award, FileText, FolderOpen } from 'lucide-react';

import { Badge } from '../ui/badge';
import { GenericDocSection } from '../DocumentSections';

export default function ArchivedDocumentsSection({ archives }) {
  const [open, setOpen] = useState(false);

  if (!archives) return null;

  const rcds = archives.attestations_rcds || [];
  const ftpe = archives.attestations_ftpe || [];
  const cond = archives.conditions || [];
  const ged = archives.ged || [];
  const total = rcds.length + ftpe.length + cond.length + ged.length;

  if (total === 0) return null;

  return (
    <div
      className="border-2 border-dashed border-slate-300 rounded-xl bg-gradient-to-br from-slate-50 to-zinc-50/60 overflow-hidden"
      data-testid="archived-documents-section"
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-100/60 transition-colors"
        data-testid="archived-toggle-btn"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-500 to-zinc-600 flex items-center justify-center shadow-sm">
            <Archive className="h-5 w-5 text-white" />
          </div>
          <div className="text-left">
            <h4 className="font-bold text-slate-900 flex items-center gap-2 text-sm">
              Documents archivés
              <Badge className="bg-slate-700 text-white border-slate-800 shadow-sm text-[10px] font-semibold">
                {total}
              </Badge>
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Contrats échus (au-delà de la date d&apos;échéance + 1 jour) — accès consultation uniquement.
            </p>
          </div>
        </div>
        {open ? (
          <ChevronUp className="h-5 w-5 text-slate-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-500 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-slate-200 pt-4" data-testid="archived-content">
          <GenericDocSection
            title="Attestations RCDS archivées"
            icon={Shield}
            iconColor="text-slate-500"
            items={rcds}
            bgColor="bg-white"
            borderColor="border-slate-200"
            textColor="text-slate-700"
          />
          <GenericDocSection
            title="Attestations FTPE archivées"
            icon={Award}
            iconColor="text-slate-500"
            items={ftpe}
            bgColor="bg-white"
            borderColor="border-slate-200"
            textColor="text-slate-700"
          />
          <GenericDocSection
            title="Conditions générales / particulières archivées"
            icon={FileText}
            iconColor="text-slate-500"
            items={cond}
            bgColor="bg-white"
            borderColor="border-slate-200"
            textColor="text-slate-700"
          />
          <GenericDocSection
            title="Documents GED archivés"
            icon={FolderOpen}
            iconColor="text-slate-500"
            items={ged}
            bgColor="bg-white"
            borderColor="border-slate-200"
            textColor="text-slate-700"
          />
        </div>
      )}
    </div>
  );
}
